/**
 * Resource file-tree primitives shared by hub-as-code and org-as-code.
 *
 * Both surfaces represent a resource's files as a directory tree on disk
 * (`<root>/<slug>/...`), scan them into `HubAsCodeResourceFile[]` (text inline,
 * binary as base64, SHA-256 hashes), and write them back. Extracted here so
 * the binary/hash/orphan-cleanup rules live in exactly one place.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as crypto from 'node:crypto';
import type { HubAsCodePayload, HubAsCodeResource, HubAsCodeResourceFile } from './types.js';
import { writeFileNoFollow, ensureRealSubdirNoSymlink, fileHasBytes, type FileChangeLog } from './fs-safety.js';
import { MAX_RESOURCE_AGGREGATE_ENCODED_BYTES } from '@wayai/core/contracts';

export const BINARY_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico',
  'mp3', 'mp4', 'wav', 'zip', 'tar', 'gz',
  'woff', 'woff2', 'ttf', 'eot',
]);

export const MAX_RESOURCE_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function isBinaryFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return BINARY_EXTENSIONS.has(ext);
}

export function guessMimeType(filename: string): string | undefined {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const mimeMap: Record<string, string> = {
    md: 'text/markdown', txt: 'text/plain', json: 'application/json',
    yaml: 'text/yaml', yml: 'text/yaml', html: 'text/html', css: 'text/css',
    js: 'text/javascript', ts: 'text/typescript', xml: 'text/xml', csv: 'text/csv',
    pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', ico: 'image/x-icon',
    mp3: 'audio/mpeg', mp4: 'video/mp4', wav: 'audio/wav',
    zip: 'application/zip', tar: 'application/x-tar', gz: 'application/gzip',
    woff: 'font/woff', woff2: 'font/woff2', ttf: 'font/ttf', eot: 'application/vnd.ms-fontobject',
  };
  return mimeMap[ext];
}

export function computeHash(data: Buffer): string {
  return crypto.createHash('sha256').update(data).digest('hex');
}

/** Recursively scan a resource directory into file entries (text inline, binary base64). */
export function scanResourceFiles(dir: string, prefix = ''): HubAsCodeResourceFile[] {
  const files: HubAsCodeResourceFile[] = [];
  const entries = fs.readdirSync(dir, { withFileTypes: true });

  for (const entry of entries) {
    const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;

    if (entry.isDirectory()) {
      files.push(...scanResourceFiles(path.join(dir, entry.name), relPath));
    } else if (entry.isFile()) {
      const fullPath = path.join(dir, entry.name);
      const stat = fs.statSync(fullPath);

      if (stat.size > MAX_RESOURCE_FILE_SIZE) {
        console.warn(`  Warning: skipping ${relPath} (${(stat.size / 1024 / 1024).toFixed(1)}MB exceeds 10MB limit)`);
        continue;
      }

      const fileEntry: HubAsCodeResourceFile = {
        path: relPath,
        mime_type: guessMimeType(entry.name),
        file_size: stat.size,
      };

      const data = fs.readFileSync(fullPath);
      fileEntry.hash = computeHash(data);

      if (isBinaryFile(entry.name)) {
        fileEntry.content_base64 = data.toString('base64');
      } else {
        fileEntry.content = data.toString('utf-8');
      }

      files.push(fileEntry);
    }
  }

  return files;
}

/**
 * Remove byte carriers when the target hub already stores the same hash. Path +
 * hash remain in the declarative payload, so the backend matches the file before
 * its deletion pass and skips it as unchanged (gh #3401).
 */
export function omitUnchangedResourceFileContent(
  localResources: HubAsCodeResource[] | undefined,
  remoteResources: HubAsCodeResource[] | undefined,
): number {
  if (!localResources?.length || !remoteResources?.length) return 0;

  const remoteById = new Map(remoteResources.flatMap((resource) => resource.id ? [[resource.id, resource] as const] : []));
  const remoteByName = new Map(remoteResources.map((resource) => [resource.name, resource] as const));
  let omitted = 0;

  for (const localResource of localResources) {
    const remoteResource = (localResource.id ? remoteById.get(localResource.id) : undefined)
      ?? remoteByName.get(localResource.name);
    if (!remoteResource?.files?.length || !localResource.files?.length) continue;

    const remoteFilesById = new Map(remoteResource.files.flatMap((file) => file.id ? [[file.id, file] as const] : []));
    const remoteFilesByPath = new Map(remoteResource.files.map((file) => [file.path, file] as const));
    for (const localFile of localResource.files) {
      const remoteFile = (localFile.id ? remoteFilesById.get(localFile.id) : undefined)
        ?? remoteFilesByPath.get(localFile.path);
      if (!localFile.hash || !remoteFile?.hash || localFile.hash !== remoteFile.hash) continue;

      if (localFile.content !== undefined) {
        delete localFile.content;
        omitted++;
      }
      if (localFile.content_base64 !== undefined) {
        delete localFile.content_base64;
        omitted++;
      }
    }
  }

  return omitted;
}

/** Build an independent wire payload whose unchanged resource files carry only metadata. */
export function prepareResourcePayload(
  local: HubAsCodePayload,
  remote: HubAsCodePayload,
): HubAsCodePayload {
  const payload = structuredClone(local);
  omitUnchangedResourceFileContent(payload.resources, remote.resources);
  return payload;
}

/** A complete fallback payload is valid only while its inline carriers fit the wire contract. */
export function assertResourcePayloadFitsWithoutPruning(payload: HubAsCodePayload, cause: unknown): void {
  let bytes = 0;
  for (const resource of payload.resources ?? []) {
    for (const file of resource.files ?? []) {
      bytes += file.content_base64?.length ?? 0;
      bytes += file.content === undefined ? 0 : Buffer.byteLength(file.content, 'utf8');
      if (bytes > MAX_RESOURCE_AGGREGATE_ENCODED_BYTES) {
        throw new Error('Could not compare this large resource payload because remote hashes are unavailable; retry the command.', { cause });
      }
    }
  }
}

/**
 * Write a resource's files into `resDir`, then prune orphans. Text and base64
 * content land synchronously; files carrying only `download_url` are left for
 * the caller's async download pass but still counted as written (not orphaned).
 *
 * Two-level containment (gh #3365): the TRUSTED `root` (hub/org folder) bounds `resDir`
 * itself — a symlinked or ancestor-symlinked `resources/<slug>` that escapes the workspace
 * is rejected — while the REAL `resDir` bounds each per-file write, so a nested subdir
 * symlink can't redirect a write ELSEWHERE inside the workspace (e.g. `<res>/docs -> hub/`
 * overwriting `hub.yaml`). A symlinked resDir can't be its own boundary because `root` is.
 */
export function writeResourceFileTree(resDir: string, files: HubAsCodeResourceFile[], root: string, log?: FileChangeLog): void {
  // resDir must be reachable from the trusted workspace `root` through NO symlinked component
  // (catches a symlinked resDir, a symlinked ANCESTOR like `resources/` — even when resDir
  // doesn't exist yet — and inward-pointing root symlinks). Create it (real dirs) when writing.
  if (!ensureRealSubdirNoSymlink(root, resDir, files.length > 0)) {
    console.warn(`  Warning: skipping resource dir (not reachable inside the workspace without a symlink)`);
    return;
  }
  if (files.length === 0) {
    if (fs.existsSync(resDir)) cleanOrphanFiles(resDir, '', new Set<string>(), root, log);
    return;
  }

  const writtenPaths = new Set<string>();
  for (const file of files) {
    const filePath = path.join(resDir, file.path);
    // Guard against path traversal (e.g. "../../../.bashrc")
    if (!filePath.startsWith(resDir + path.sep) && filePath !== resDir) {
      console.warn(`  Warning: skipping ${file.path} (path traversal detected)`);
      continue;
    }
    writtenPaths.add(file.path);

    // download_url files carry no inline bytes here — they're fetched later by
    // downloadBinaryFiles (still reserved above so they aren't orphan-pruned).
    const buf = file.content !== undefined
      ? Buffer.from(file.content, 'utf-8')
      : file.content_base64
        ? Buffer.from(file.content_base64, 'base64')
        : undefined;
    if (buf === undefined) continue;

    // Idempotent: skip a byte-identical write (no mtime churn); the file stays reserved in
    // writtenPaths so it isn't orphan-pruned. fileHasBytes is lstat-guarded, so a symlinked
    // destination reads as "not identical" and falls through to the no-follow write below.
    if (fileHasBytes(filePath, buf)) continue;

    // writeFileNoFollow verifies the parent dir resolves inside `resDir` and never follows a
    // symlinked destination — a `resources/docs/x.pdf -> /tmp/victim` link or a `docs -> hub/`
    // subdir symlink must not redirect the write out of the resource folder (gh #3365).
    if (writeFileNoFollow(resDir, filePath, buf)) {
      log?.changed.push(path.relative(root, filePath));
    } else {
      console.warn(`  Warning: skipping ${file.path} (destination escapes the resource folder via a symlink)`);
      writtenPaths.delete(file.path);
    }
  }

  if (fs.existsSync(resDir)) cleanOrphanFiles(resDir, '', writtenPaths, root, log);
}

/**
 * Recursively remove files not in `writtenPaths`, plus any directories left empty. When
 * `root` + `log` are provided, each removed FILE is recorded (hub-relative) into `log.removed`;
 * empty-dir tidy-up rmdirs are not recorded (they hold no user content). Callers that only
 * prune (no announcement) may omit both.
 */
export function cleanOrphanFiles(dir: string, prefix: string, writtenPaths: Set<string>, root?: string, log?: FileChangeLog): void {
  const entries = fs.readdirSync(dir, { withFileTypes: true });

  for (const entry of entries) {
    const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;
    const fullPath = path.join(dir, entry.name);

    if (entry.isDirectory()) {
      cleanOrphanFiles(fullPath, relPath, writtenPaths, root, log);
      if (fs.readdirSync(fullPath).length === 0) fs.rmdirSync(fullPath);
    } else if (!writtenPaths.has(relPath)) {
      fs.unlinkSync(fullPath);
      if (log && root) log.removed.push(path.relative(root, fullPath));
    }
  }
}

/**
 * Download binary files (those carrying `download_url` and no inline content)
 * into `resDir`. Returns the count downloaded. Path-traversal guarded.
 *
 * Two-level containment (gh #3365): the TRUSTED `root` (hub/org folder) bounds `resDir`
 * itself; the REAL `resDir` bounds each per-file write (so a nested subdir symlink can't
 * redirect a write elsewhere in the workspace).
 */
export async function downloadBinaryFiles(resDir: string, files: HubAsCodeResourceFile[], root: string, log?: FileChangeLog): Promise<number> {
  // resDir must be reachable from the trusted workspace `root` through no symlinked component
  // (incl. a symlinked ancestor when resDir doesn't exist yet). Create it real; write below is
  // then bounded by resDir.
  if (!ensureRealSubdirNoSymlink(root, resDir, true)) {
    console.warn(`  Warning: skipping resource downloads (dir not reachable inside the workspace without a symlink)`);
    return 0;
  }
  let count = 0;
  for (const file of files) {
    if (!file.download_url || file.content !== undefined) continue;
    try {
      const filePath = path.join(resDir, file.path);
      if (!filePath.startsWith(resDir + path.sep) && filePath !== resDir) {
        console.warn(`  Warning: skipping ${file.path} (path traversal detected)`);
        continue;
      }
      // Idempotent: skip when the local REAL file already matches by content hash — keeps repeat
      // pulls cheap and keeps the change log honest. lstat: never hash through a symlink; a
      // symlinked destination falls through to the no-follow write below.
      if (file.hash) {
        try {
          const st = fs.lstatSync(filePath);
          if (st.isFile() && !st.isSymbolicLink() && computeHash(fs.readFileSync(filePath)) === file.hash) continue;
        } catch { /* absent → download */ }
      }
      const response = await fetch(file.download_url);
      if (!response.ok) {
        console.warn(`  Warning: failed to download ${file.path} (${response.status})`);
        continue;
      }
      const buffer = Buffer.from(await response.arrayBuffer());
      // Idempotent for hashless rows too (backend `file_hash` may be null): the hash-skip above
      // can't fire, so compare the fetched bytes against the local real file and skip the write
      // when they already match — no mtime churn, no false `changed` entry on a repeat pull.
      // lstat-guarded (a symlinked destination reads as "not identical" → falls through to the
      // no-follow write below).
      if (fileHasBytes(filePath, buffer)) continue;
      // No-follow write bounded by the REAL resource dir: refuse a symlinked destination or a
      // nested subdir symlink that redirects out of resDir (even back into the workspace).
      if (writeFileNoFollow(resDir, filePath, buffer)) {
        count++;
        log?.changed.push(path.relative(root, filePath));
      } else {
        console.warn(`  Warning: skipping ${file.path} (destination escapes the resource folder via a symlink)`);
      }
    } catch (err) {
      console.warn(`  Warning: failed to download ${file.path} (${err instanceof Error ? err.message : String(err)})`);
    }
  }
  return count;
}
